import React from 'react';
import type { HorseHealth } from '../../../../types/horseProfile';

interface HealthEditFormProps {
  initialData: HorseHealth;
  onSubmit: (data: HorseHealth) => Promise<void>;
}

const DEFAULT_HEALTH_DATA: HorseHealth = {
  diet: {
    feedingSchedule: '',
    specialDiet: '',
    supplements: [],
    restrictions: []
  },
  medicalHistory: [],
  conditions: [],
  lastVetVisit: null,
  nextVetVisit: null
};

export const HealthEditForm: React.FC<HealthEditFormProps> = ({
  initialData = DEFAULT_HEALTH_DATA,
  onSubmit
}) => {
  const [formData, setFormData] = React.useState<HorseHealth>({
    ...DEFAULT_HEALTH_DATA,
    ...initialData
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const addMedicalRecord = () => {
    setFormData(prev => ({
      ...prev,
      medicalHistory: [
        ...prev.medicalHistory,
        {
          date: new Date().toISOString().split('T')[0],
          type: 'checkup',
          description: '',
          veterinarian: ''
        }
      ]
    }));
  };

  const removeMedicalRecord = (index: number) => {
    setFormData(prev => ({
      ...prev,
      medicalHistory: prev.medicalHistory.filter((_, i) => i !== index)
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-4">Ernährung</h4>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Fütterungsplan
            </label>
            <textarea
              value={formData.diet.feedingSchedule}
              onChange={(e) => setFormData({
                ...formData,
                diet: { ...formData.diet, feedingSchedule: e.target.value }
              })}
              rows={3}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Ergänzungsfutter
            </label>
            <input
              type="text"
              value={formData.diet.supplements?.join(', ') || ''}
              onChange={(e) => setFormData({
                ...formData,
                diet: {
                  ...formData.diet,
                  supplements: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                }
              })}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
              placeholder="Mit Komma getrennt eingeben"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Einschränkungen
            </label>
            <input
              type="text"
              value={formData.diet.restrictions?.join(', ') || ''}
              onChange={(e) => setFormData({
                ...formData,
                diet: {
                  ...formData.diet,
                  restrictions: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                }
              })}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
              placeholder="Mit Komma getrennt eingeben"
            />
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white">Medizinische Historie</h4>
          <button
            type="button"
            onClick={addMedicalRecord}
            className="px-3 py-1 text-sm text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
          >
            + Eintrag hinzufügen
          </button>
        </div>
        
        <div className="space-y-4">
          {formData.medicalHistory.map((record, index) => (
            <div key={index} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Datum
                  </label>
                  <input
                    type="date"
                    value={record.date}
                    onChange={(e) => {
                      const newHistory = [...formData.medicalHistory];
                      newHistory[index] = { ...record, date: e.target.value };
                      setFormData({ ...formData, medicalHistory: newHistory });
                    }}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Typ
                  </label>
                  <select
                    value={record.type}
                    onChange={(e) => {
                      const newHistory = [...formData.medicalHistory];
                      newHistory[index] = { ...record, type: e.target.value as any };
                      setFormData({ ...formData, medicalHistory: newHistory });
                    }}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                    required
                  >
                    <option value="checkup">Kontrolle</option>
                    <option value="vaccination">Impfung</option>
                    <option value="injury">Verletzung</option>
                    <option value="treatment">Behandlung</option>
                  </select>
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Beschreibung
                </label>
                <textarea
                  value={record.description}
                  onChange={(e) => {
                    const newHistory = [...formData.medicalHistory];
                    newHistory[index] = { ...record, description: e.target.value };
                    setFormData({ ...formData, medicalHistory: newHistory });
                  }}
                  rows={2}
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                  required
                />
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Tierarzt
                </label>
                <input
                  type="text"
                  value={record.veterinarian || ''}
                  onChange={(e) => {
                    const newHistory = [...formData.medicalHistory];
                    newHistory[index] = { ...record, veterinarian: e.target.value };
                    setFormData({ ...formData, medicalHistory: newHistory });
                  }}
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={() => removeMedicalRecord(index)}
                  className="text-sm text-red-600 hover:text-red-500 dark:text-red-400 dark:hover:text-red-300"
                >
                  Eintrag entfernen
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Letzter Tierarztbesuch
          </label>
          <input
            type="date"
            value={formData.lastVetVisit || ''}
            onChange={(e) => setFormData({ ...formData, lastVetVisit: e.target.value })}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Nächster Tierarztbesuch
          </label>
          <input
            type="date"
            value={formData.nextVetVisit || ''}
            onChange={(e) => setFormData({ ...formData, nextVetVisit: e.target.value })}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
          />
        </div>
      </div>
    </form>
  );
};